/* eslint-disable */
import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import { homeAddressCheckRegex } from '../utils/acquisition-utils';
import { useWhitelistContext } from '../providers/WhitelistProvider/WhitelistProvider';
import { getApiToken } from '../utils/cob-utils';

type AddressCache = {
    suggestedAddress?: {};
    address: string;
    isValid: boolean;
    isPOBoxOrCommercial: boolean;
    data: any;
}

type AddressType = 'mailing' | 'home' | 'Mailing' | 'Home';

type LoqateQueryMatch = {
    AdministrativeArea: string;
    Locality: string;
} & Record<string, unknown>

type LoqateQueryResponse = {
    Matches: LoqateQueryMatch[]
}

export default function useLoqate() {
    const [pca, setPca] = useState<any>();
    const [loqateEnabled, setLoqateEnabled] = useState(false);
    const [controls, setControls] = useState<any[]>([]);
    const [popupShown, setPopupShown] = useState(false);
    const { isAddressWhitelisted } = useWhitelistContext();
    const invalidCodes = ['AC01', 'AC02', 'AC03', 'AC07', 'AC09', 'AC10', 'AC11', 'AC12', 'AC14', 'AE10', 'AE11'];
    const commercialCodes = ['AS10', 'AS11', 'AS20', 'AE13', 'AE14', 'AE12'];
    const loqateKey = useRef('');
    const addressCacheRef = useRef<Record<string, AddressCache>>({});

    const generateCacheKey = (addressType: AddressType, addressValues: string[]) => {
        const addressString = addressValues.map((value) => value.toLowerCase().trim()).join('-');
        return `${addressType}-${addressString}`
    };

    const addToCache = (cacheKey, data: AddressCache) => {
        addressCacheRef.current = {
            ...addressCacheRef.current,
            [cacheKey]: data,
        }
    }

    useEffect(() => {
        const loqateExists = !!document.querySelector('body #loqate') || !!document.querySelector('head #loqate');
        setLoqateEnabled(loqateExists);

        if (!loqateExists) {
            return;
        }
        const token = getApiToken('loqateKey');
        if (!token) {
            return;
        }
        loqateKey.current = token;

        if (loqateExists && loqateKey.current) {
            const interval = setInterval(() => {
                if ((window as any).pca) {
                    setPca((window as any).pca);
                    clearInterval(interval);
                }
            }, 100);

            return () => clearInterval(interval);
        }

    }, []);


    const initAutoComplete = (addressLine1: string, addressLine2: string, city: string, state: string, zip: string) => {
        if (pca !== undefined) {
            //Fields for home address Autocomplete
            const fields = [
                { element: addressLine1, field: 'Line1' },
                { element: addressLine2, field: 'SubBuilding', mode: pca.fieldMode.POPULATE },
                { element: city, field: 'City', mode: pca.fieldMode.POPULATE },
                { element: state, field: 'Province', mode: pca.fieldMode.POPULATE },
                { element: zip, field: 'PostalCode', mode: pca.fieldMode.POPULATE },
            ];

            //Loqate API options
            const options = {
                key: loqateKey.current,
                search: { countries: 'USA' },
                suppressAutocomplete: false,
            };
            //Control object for home address Autocomplete
            const control = new pca.Address(fields, options);
            setControls([...controls, control]);

            return control;
        }
    };

    const setBasicListeners = (control: any) => {

        control.listen('results', function (results: any) {

            for (let i = 0; i < results.length; i++) {
                if (
                    typeof results[i] !== 'undefined' &&
                    (results[i].Text.indexOf('PO Box') !== -1 || results[i].Text.indexOf('P O Box') !== -1)
                ) {
                    results.splice(i);
                }
            }
        });

        //Handle formatting addresses for autocomplete
        control.listen('prepopulate', function (address: any) {
            address.PostalCode = address.PostalCode.substring(0, 5);
            address.Line1 = address.Line1.replace(address.SubBuilding, '');
            address.FormattedLine1 = address.Line2
                ? address.Line2.replace(address.SubBuilding, '')
                : address.FormattedLine1.replace(address.SubBuilding, '');
        });
    };

    const buildFullAddress = (addressLine1?: string, addressLine2?: string, city?: string, state?: string, zip?: string) => {
        const fullAddressArr = [addressLine1, addressLine2, city, state, zip];
        return fullAddressArr
            .filter(Boolean)
            .join(' ')
            .replace(/[^a-zA-Z0-9]/g, '')
            .toLocaleLowerCase();
    }

    const hideLoqate = (control: any) => {
        if (!document.querySelector('.pcaselected:hover')) {
            control.destroy();
            control.load();
        }
    };

    const isValidAddress = async (addressLine1?: string, addressLine2?: string, city?: string, state?: string, zip?: string) => {

        const header = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const body = {
            Key: loqateKey.current,
            Addresses: [
                {
                    Address1: `${addressLine1 ?? ''} ${addressLine2 ?? ''}`,
                    SubBuilding: '',
                    Locality: city ?? '',
                    AdministrativeArea: state ?? '',
                    PostalCode: zip ?? '',
                    Country: 'USA',
                },
            ],
            Options: {
                Certify: true,
                Enhance: true,
                ServerOptions: {
                    CertifiedCountryList: 'USA',
                    ActiveCASSLibrary: 'CASS2',
                },
            },
        };

        const fullAddress = buildFullAddress(addressLine1, addressLine2, city, state, zip);
        if (isAddressWhitelisted(fullAddress)) {
            return true;
        }

        const validity: boolean = await axios.post('https://api.addressy.com/Cleansing/International/Batch/v1.00/json4.ws', body, header).then((response: any) => {
            const data = response.data;
            let isValid = true;
            /*  TODO: simplify this conditional if possible for valid loqate responses (200) that return an error
            *   
            *   Context for length !== 2; all valid address verifications are of length 2, so any loqate response
            *   that is not length 2 is some kind of error or invalid response that isn't parsable by the logic below
            */
            if (!response || !response.data || !response.data[0] || Object.keys(response.data[0]).length !== 2) {
                return true;
            }

            const addressMatch = data[0].Matches[0];
            const DPVIndicator = addressMatch.DPVConfirmedIndicator;
            if (DPVIndicator !== 'Y' && DPVIndicator !== 'D' && DPVIndicator !== 'S') {
                isValid = false;
            }

            invalidCodes.forEach((code) => {
                if (addressMatch.ResultsString.includes(code)) {
                    isValid = false;
                }
            });

            return isValid;
        }).catch((error) => {
            console.error(error);
            return true;
        });
        return validity;
    };

    const isCommercialAddress = async (addressLine1?: string, addressLine2?: string, city?: string, state?: string, zip?: string) => {
        const fullAddress = buildFullAddress(addressLine1, addressLine2, city, state, zip)
        if (isAddressWhitelisted(fullAddress)) {
            return false;
        }

        const header = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const body = {
            Key: loqateKey.current,
            Addresses: [
                {
                    Address1: `${addressLine1 ?? ''} ${addressLine2 ?? ''}`,
                    SubBuilding: '',
                    Locality: city ?? '',
                    AdministrativeArea: state ?? '',
                    PostalCode: zip ?? '',
                    Country: 'USA',
                },
            ],
            Options: {
                Certify: true,
                Enhance: true,
                ServerOptions: {
                    CertifiedCountryList: 'USA',
                    ActiveCASSLibrary: 'CASS2',
                },
            },
        };


        const addressData = await axios.post('https://api.addressy.com/Cleansing/International/Batch/v1.00/json4.ws', body, header).then((response: any) => {
            return response.data;
        }).catch((error) => {
            console.error(error);
            return null;
        });

        if (addressData !== null) {
            let codeFound: boolean;

            return commercialCodes.some((code) => {
                return addressData[0].Matches[0].ResultsString.includes(code) || (addressData[0].Matches[0].ResidentialDelivery === 'N' || addressData[0].Matches[0].ResidentialDelivery === ' ' || addressData[0].Matches[0].RecordType === 'P' || codeFound);
            });
        }

        return false;
    };

    const searchAddressByZip = async (zip?: string) : Promise<LoqateQueryMatch> =>  {
        const header = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const body = {
            Key: loqateKey.current,
            Options: {
                Process: 'Query',
                ServerOptions: {
                    'Table': 'rd_US_vfy',
                    'QueryString': `(PostalCodePrimary LIKE \"%${zip}%\")`,
                    'OutputFields': 'AdministrativeArea,Locality',
                    'QueryClause': 'Distinct',
                    'AliasPreference': 'First',
                    'RangefieldPreference': 'Match',
                    'MaxResults': '100'
                }
            },
        };

        return await axios.post('https://api.addressy.com/Cleansing/International/Batch/v1.00/json4.ws', body, header).then((response: { data: LoqateQueryResponse }) => {
            return response.data[0].Matches[0];
        }).catch((error) => {
            console.error(error);
            return { AdministrativeArea: '', Locality: ''};
        }); 
    }

    const getAddressData = async (addressLine1?: string, addressLine2?: string, city?: string, state?: string, zip?: string, type?: string, useSuggestion?: boolean, avcThreshold?: string): Promise<{ valid: boolean, commercial: boolean, suggestedAddress: any } | null> => {
        const header = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const body = {
            Key: loqateKey.current,
            Addresses: [
                {
                    Address1: `${addressLine1 ?? ''} ${addressLine2 ?? ''}`,
                    SubBuilding: '',
                    Locality: city ?? '',
                    AdministrativeArea: state ?? '',
                    PostalCode: zip ?? '',
                    Country: 'USA',
                },
            ],
            Options: {
                Certify: true,
                Enhance: true,
                ServerOptions: {
                    CertifiedCountryList: 'USA',
                    ActiveCASSLibrary: 'CASS2',
                },
            },
        };

        const addressData = {
            valid: false,
            commercial: false,
            suggestedAddress: undefined,
        };

        const fullAddress = buildFullAddress(addressLine1, addressLine2, city, state, zip)
        if (isAddressWhitelisted(fullAddress)) {
            addressData.valid = true;
            return addressData;
        }

        return await axios.post('https://api.addressy.com/Cleansing/International/Batch/v1.00/json4.ws', body, header).then((response: any) => {
            /*  TODO: simplify this conditional if possible for valid loqate responses (200) that return an error
            *   
            *   Context for length !== 2; all valid address verifications are of length 2, so any loqate response
            *   that is not length 2 is some kind of error or invalid response that isn't parsable by the logic below
            */
            if (!response || !response.data || !response.data[0] || Object.keys(response.data[0]).length !== 2) {
                addressData.valid = true;
                if (addressLine1) {
                    addressData.commercial = homeAddressCheckRegex.test(addressLine1);
                } else {
                    addressData.commercial = false;
                }
                return addressData;
            }

            const data = response.data;
            const match = data[0].Matches[0];
            const DPVIndicator = match.DPVConfirmedIndicator;
            const avc = parseInt(match.AVC.split('-')[3]);
            const threshold = avcThreshold ? parseInt(avcThreshold) : 95;
            let isValid = true;
            if (DPVIndicator !== 'Y' && DPVIndicator !== 'D' && DPVIndicator !== 'S') {
                isValid = false;
            }

            if (data !== null) {
                if (
                    data[0].Matches[0].ResidentialDelivery !== 'Y' ||
                    data[0].Matches[0].RecordType === 'P'
                ) {
                    addressData.commercial = true;
                    if (type === 'home') {
                        isValid = false;
                    }
                } else {
                    addressData.commercial = addressLine1 ? homeAddressCheckRegex.test(addressLine1) : false;
                }
            }

            invalidCodes.forEach((code) => {
                if (match.ResultsString.includes(code)) {
                    isValid = false;
                    if (code.includes('AC') && avc >= threshold && useSuggestion) {
                        addressData.suggestedAddress = {
                            addressLine1: match.PrimaryAddressLine.replace(match.SubBuilding, '').trim(),
                            addressLine2: match.SubBuilding,
                            city: match.Locality,
                            state: match.AdministrativeArea,
                            zipCode: match.PostalCodePrimary
                        };
                    }
                }
            });

            commercialCodes.forEach((code) => {
                if (type === 'mailing') {
                    if (match.ResultsString.includes(code)) {
                        addressData.commercial = true;
                    }
                } else if (type === 'home') {
                    if (match.ResultsString.includes(code)) {
                        isValid = false;
                        addressData.commercial = true;
                    }
                }
            });

            if (!isValid) {
                addressData.valid = false;
                if (addressLine1) {
                    addressData.commercial = homeAddressCheckRegex.test(addressLine1);
                } else {
                    addressData.commercial = false;
                }
            } else {
                addressData.valid = true;
            }
            return addressData;

        }).catch((error) => {
            console.error(error);
            addressData.valid = true;
            if (addressLine1) {
                addressData.commercial = homeAddressCheckRegex.test(addressLine1);
            } else {
                addressData.commercial = false;
            }
            return addressData;

        });
    };

    return {
        initAutoComplete,
        setBasicListeners,
        isValidAddress,
        isCommercialAddress,
        searchAddressByZip,
        getAddressData,
        hideLoqate,
        generateCacheKey,
        addToCache,
        addressCacheRef,
        controls,
        pca,
        loqateEnabled,
        loqateKey,
        popupShown,
        setPopupShown,
    };
}